// src/pages/Auth/Login/Login.jsx
import axios from "axios";
import { useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { CiLock, CiMail } from "react-icons/ci";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logo from "../../../Images/logo1.png";
import { login as loginAction } from "../../../redux/authSlice";
import styles from "./Login.module.css";


const API =
  process.env.REACT_APP_API_BASE ||
  "https://prod.toxboxai.com";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ---- Login state ----
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // ---- Forgot Password modal state ----
  const [showForgot, setShowForgot] = useState(false);
  const [fpStep, setFpStep] = useState(1);
  const [fpEmail, setFpEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const loginURL = `${API}/api/auth/login`;

  // ----------------- LOGIN SUBMIT -----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    // const passwordRegex =
    //   /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    // if (!passwordRegex.test(password)) {
    //   setError(
    //     "Password must be at least 8 characters long and include one letter, one number, and one special character."
    //   );
    //   setIsLoading(false);
    //   return;
    // }

    try {
      const response = await axios.post(loginURL, {
        email: email.toLowerCase(),
        password,
      });

      const data = response.data || {};
      localStorage.setItem("authData", JSON.stringify(data));

      const { token, user: userData, message } = data;
      if (!token || !userData || !userData.id || !userData.role) {
        setError(message || "Login failed");
        setIsLoading(false);
        return;
      }

      dispatch(
        loginAction({ token, role: userData.role, userId: userData.id })
      );
      navigate("/dashboard");
    } catch (err) {
      setError("An error occurred during login.");
    } finally {
      setIsLoading(false);
    }
  };

  // ----------------- FORGOT PASSWORD HELPERS -----------------
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const passRules = {
    length: newPass.length >= 8,
    uppercase: /[A-Z]/.test(newPass),
    lowercase: /[a-z]/.test(newPass),
    number: /\d/.test(newPass),
    special: /[!@#$%^&*]/.test(newPass),
  };
  const isValidNewPassword =
    passRules.length &&
    passRules.uppercase &&
    passRules.lowercase &&
    passRules.number &&
    passRules.special;

  // Step 1: Request OTP
  const handleRequestOtp = async () => {
    if (!fpEmail || !isValidEmail(fpEmail)) {
      toast.error("Please enter a valid email!");
      return;
    }
    setFpLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/forgot-password`, {
        email: fpEmail,
      });
      toast.success(res.data.message);
      setFpStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error sending OTP");
    } finally {
      setFpLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp) {
      toast.error("Please enter OTP!");
      return;
    }
    setFpLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/verify-otp`, {
        email: fpEmail,
        otp,
      });
      toast.success(res.data.message);
      setResetToken(res.data.resetToken);
      setFpStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP");
    } finally {
      setFpLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async () => {
    if (!isValidNewPassword) {
      toast.error(
        "Password must be at least 8 characters, include uppercase, lowercase, number & special character."
      );
      return;
    }
    if (newPass !== confirmPass) {
      toast.error("Passwords do not match. Enter the same password above.");
      return;
    }

    setFpLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/auth/reset-password/${resetToken}`,
        { password: newPass }
      );
      toast.success(res.data.message);
      setFpStep(4);
    } catch (err) {
      const msg = err.response?.data?.message;
      if (msg?.toLowerCase().includes("same")) {
        toast.error(
          "❌ New password cannot be the same as your previous password."
        );
      } else {
        toast.error(msg || "Error resetting password");
      }
    } finally {
      setFpLoading(false);
    }
  };

  const closeForgot = () => {
    setShowForgot(false);
    // reset forgot state so it’s clean next time
    setFpStep(1);
    setFpEmail("");
    setOtp("");
    setResetToken("");
    setNewPass("");
    setConfirmPass("");
    setShowNewPass(false);
    setShowConfirmPass(false);
  };

  return (
    <div className={styles.container}>
      <ToastContainer position="top-right" />
      <div className={styles.formWrapper}>
        <img className={styles.logoimg} src={logo} alt="Logo" />
        <h3>Sign in to your account</h3>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className={styles.Logininputgroup}>
            <span className={styles.LogininputIcon}>
              <CiMail />
            </span>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              required
              disabled={isLoading}
            />
          </div>

          {/* <div
            className={styles.Logininputgroup}
            style={{ position: "relative" }}
          >
            <span className={styles.LogininputIcon}>
              <CiLock />
            </span>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              minLength={8}
              pattern="^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
              title="Password must be at least 8 characters long, include one letter, one number, and one special character"
            />
            <span className={styles.eyeIcon} onClick={(e) => e.target.value}>
              {showNewPass ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
            </span>
          </div> */}
          <div className={styles.Logininputgroup}>
            <span className={styles.LogininputIcon}>
              <CiLock />
            </span>
            <input
              type={showNewPass ? "text" : "password"}
              placeholder="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              disabled={isLoading}
            // minLength={8}
            // pattern="^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
            // title="Password must be at least 8 characters long, include one letter, one number, and one special character"
            />
            <span
              className={styles.eyeIcon}
              onClick={() => setShowNewPass((s) => !s)}
            >
              {showNewPass ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
            </span>
          </div>

          <div className={styles.checkContainer}>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setShowForgot(true)} // << open modal
              disabled={isLoading}
            >
              Forgot Password?
            </button>
          </div>

          <button className={styles.button} type="submit" disabled={isLoading}>
            {isLoading ? (
              <div className={styles.loadingballs}>
                <div></div>
                <div></div>
                <div></div>
              </div>
            ) : (
              "Login"
            )}
          </button>
        </form>
      </div>

      {/* ======= Forgot Password Modal ======= */}
      {showForgot && (
        <div className={styles.forgotOverlay} onClick={closeForgot}>
          <div
            className={styles.forgotCard}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.forgotHeader}>
              <h4>Forgot Password</h4>
              <button className={styles.closeBtn} onClick={closeForgot}>
                ✕
              </button>
            </div>

            {fpStep === 1 && (
              <>
                <div className={styles.Logininputgroup}>
                  <span className={styles.LogininputIcon}>
                    <CiMail />
                  </span>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    className={styles.input}
                    disabled={fpLoading}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleRequestOtp}
                  className={styles.button}
                  disabled={fpLoading}
                >
                  {fpLoading ? (
                    <div className={styles.loadingballs}>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  ) : (
                    "Send OTP"
                  )}
                </button>
              </>
            )}

            {fpStep === 2 && (
              <>
                <div className={styles.Logininputgroup}>
                  <input
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className={styles.input}
                    disabled={fpLoading}
                  />
                </div>

                <div className={styles.fpActions}>
                  <button
                    type="button"
                    className={styles.linkButton}
                    disabled={fpLoading}
                    onClick={handleRequestOtp}
                    title="Resend OTP to your email"
                  >
                    Resend OTP
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  className={styles.button}
                  disabled={fpLoading}
                >
                  {fpLoading ? (
                    <div className={styles.loadingballs}>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  ) : (
                    "Verify OTP"
                  )}
                </button>
              </>
            )}

            {fpStep === 3 && (
              <>
                {/* New Password */}
                <div
                  className={styles.Logininputgroup}
                  style={{ position: "relative" }}
                >
                  <span className={styles.LogininputIcon}>
                    <CiLock />
                  </span>
                  <input
                    type={showNewPass ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPass}
                    onChange={(e) => setNewPass(e.target.value)}
                    className={styles.input}
                    disabled={fpLoading}
                  />
                  <span
                    className={styles.eyeIcon}
                    onClick={() => setShowNewPass((s) => !s)}
                  >
                    {showNewPass ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                  </span>
                </div>

                {/* ✅ Show password rules only if user typed something */}
                {newPass && (
                  <ul className={styles.rules}>
                    <li
                      className={
                        passRules.length ? styles.valid : styles.invalid
                      }
                    >
                      {passRules.length ? "✅" : "❌"} At least 8 characters
                    </li>
                    <li
                      className={
                        passRules.uppercase ? styles.valid : styles.invalid
                      }
                    >
                      {passRules.uppercase ? "✅" : "❌"} One uppercase letter
                    </li>
                    <li
                      className={
                        passRules.lowercase ? styles.valid : styles.invalid
                      }
                    >
                      {passRules.lowercase ? "✅" : "❌"} One lowercase letter
                    </li>
                    <li
                      className={
                        passRules.number ? styles.valid : styles.invalid
                      }
                    >
                      {passRules.number ? "✅" : "❌"} One number
                    </li>
                    <li
                      className={
                        passRules.special ? styles.valid : styles.invalid
                      }
                    >
                      {passRules.special ? "✅" : "❌"} One special character
                      (!@#$%^&*)
                    </li>
                  </ul>
                )}

                {/* Confirm Password */}
                <div
                  className={styles.Logininputgroup}
                  style={{ position: "relative" }}
                >
                  <span className={styles.LogininputIcon}>
                    <CiLock />
                  </span>
                  <input
                    type={showConfirmPass ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPass}
                    onChange={(e) => setConfirmPass(e.target.value)}
                    className={styles.input}
                    disabled={fpLoading}
                  />
                  <span
                    className={styles.eyeIcon}
                    onClick={() => setShowConfirmPass((s) => !s)}
                  >
                    {showConfirmPass ? (
                      <AiOutlineEyeInvisible />
                    ) : (
                      <AiOutlineEye />
                    )}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleResetPassword}
                  className={styles.button}
                  disabled={fpLoading}
                >
                  {fpLoading ? (
                    <div className={styles.loadingballs}>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              </>
            )}

            {fpStep === 4 && (
              <div className={styles.successBox}>
                <p className={styles.success}>
                  🎉 Password reset successful! Please login.
                </p>
                <button
                  type="button"
                  className={styles.button}
                  onClick={closeForgot}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
